import { useState } from "react";
import {
  useRunGridBacktestMutation,
  useGetGridBacktestRunsQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function GridBacktest() {
  const [symbol, setSymbol] = useState("BTC/USDT");
  const [preset, setPreset] = useState<"conservative" | "moderate" | "aggressive">("moderate");
  const [days, setDays] = useState(180);
  const [equity, setEquity] = useState(1000);
  const [run, runState] = useRunGridBacktestMutation();
  const list = useGetGridBacktestRunsQuery(20, { pollingInterval: 30000 });

  async function go() {
    await run({ preset, days, equity, symbol } as any).unwrap();
    list.refetch();
  }

  const summary = (runState.data as any)?.summary;
  const runs = list.data?.runs || [];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Grid Backtest</h1>
        <p className="text-sm text-muted">
          Walk historical BTC OHLCV bar-by-bar, simulating fills when high/low crosses
          a grid level. Tracks realized P&L, win rate, profit factor, Sharpe, max drawdown.
          Use this to validate a preset before going live.
        </p>
      </header>

      <Card>
        <CardHeader><CardTitle className="text-base">New backtest</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <label className="text-sm">
              <div className="text-muted text-xs mb-1">Symbol</div>
              <input type="text"
                     className="w-full bg-bg text-fg border border-border rounded px-2 py-1.5 font-mono"
                     value={symbol}
                     onChange={(e) => setSymbol(e.target.value.toUpperCase().trim())} />
            </label>
            <label className="text-sm">
              <div className="text-muted text-xs mb-1">Preset</div>
              <select className="w-full bg-bg text-fg border border-border rounded px-2 py-1.5"
                      value={preset} onChange={(e) => setPreset(e.target.value as any)}>
                <option value="conservative">conservative (1H, tight)</option>
                <option value="moderate">moderate (4H, balanced)</option>
                <option value="aggressive">aggressive (1D, wide)</option>
              </select>
            </label>
            <label className="text-sm">
              <div className="text-muted text-xs mb-1">Days of history</div>
              <input type="number" min={30} max={730} step={30}
                     className="w-full bg-bg text-fg border border-border rounded px-2 py-1.5"
                     value={days} onChange={(e) => setDays(parseInt(e.target.value) || 180)} />
            </label>
            <label className="text-sm">
              <div className="text-muted text-xs mb-1">Initial equity (USDT)</div>
              <input type="number" min={100} max={1000000} step={100}
                     className="w-full bg-bg text-fg border border-border rounded px-2 py-1.5"
                     value={equity} onChange={(e) => setEquity(parseFloat(e.target.value) || 1000)} />
            </label>
          </div>
          <Button onClick={go} disabled={runState.isLoading}>
            {runState.isLoading ? "Running…" : "Run backtest"}
          </Button>
        </CardContent>
      </Card>

      {summary && (
        <Card>
          <CardHeader><CardTitle className="text-base">Latest result · {summary.preset}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="Days covered" value={String(summary.days_covered)} />
              <Stat label="Timeframe" value={summary.timeframe} />
              <Stat label="Total fills" value={String(summary.total_fills)} />
              <Stat label="Round trips" value={String(summary.round_trip_count)} />
              <Stat label="Win rate" value={`${summary.win_rate_pct}%`}
                    tone={summary.win_rate_pct >= 70 ? "good" : "warn"} />
              <Stat label="Profit factor" value={summary.profit_factor != null ? String(summary.profit_factor) : "∞"}
                    tone={summary.profit_factor >= 1.5 ? "good" : summary.profit_factor < 1 ? "bad" : "warn"} />
              <Stat label="Sharpe" value={String(summary.sharpe)}
                    tone={summary.sharpe >= 1.5 ? "good" : summary.sharpe < 0 ? "bad" : "warn"} />
              <Stat label="Max DD" value={`${summary.max_dd_pct}%`}
                    tone={summary.max_dd_pct < 5 ? "good" : summary.max_dd_pct < 15 ? "warn" : "bad"} />
              <Stat label="Total P&L" value={`$${summary.total_pnl_usd}`}
                    tone={summary.total_pnl_usd > 0 ? "good" : "bad"} />
              <Stat label="Monthly est." value={`${summary.expected_monthly_pct}%`} />
              <Stat label="Activations" value={String(summary.regime_activations)} />
              <Stat label="Disables" value={String(summary.regime_disables)} />
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Previous runs</CardTitle></CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="text-sm text-muted">No runs yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted">
                    <th className="py-1 pr-3">When</th>
                    <th className="py-1 pr-3">Symbol</th>
                    <th className="py-1 pr-3">Preset</th>
                    <th className="py-1 pr-3">TF</th>
                    <th className="py-1 pr-3 text-right">Fills</th>
                    <th className="py-1 pr-3 text-right">Win%</th>
                    <th className="py-1 pr-3 text-right">PF</th>
                    <th className="py-1 pr-3 text-right">Sharpe</th>
                    <th className="py-1 pr-3 text-right">DD%</th>
                    <th className="py-1 pr-3 text-right">P&L</th>
                    <th className="py-1 pr-3 text-right">~Monthly</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r: any) => {
                    let p = "?";
                    try { p = JSON.parse(r.params_json).preset || "?"; } catch {}
                    return (
                      <tr key={r.id} className="border-t border-border/40">
                        <td className="py-1 pr-3 text-xs text-muted">{r.ts?.slice(0, 19).replace("T", " ") || "—"}</td>
                        <td className="py-1 pr-3 font-mono text-xs">{r.symbol || "?"}</td>
                        <td className="py-1 pr-3"><Badge>{p}</Badge></td>
                        <td className="py-1 pr-3">{r.timeframe}</td>
                        <td className="py-1 pr-3 text-right">{r.total_fills}</td>
                        <td className="py-1 pr-3 text-right">{r.win_rate_pct}%</td>
                        <td className="py-1 pr-3 text-right">{r.profit_factor ?? "∞"}</td>
                        <td className="py-1 pr-3 text-right">{r.sharpe}</td>
                        <td className="py-1 pr-3 text-right">{r.max_dd_pct}%</td>
                        <td className={`py-1 pr-3 text-right ${r.total_pnl_usd > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          ${r.total_pnl_usd}
                        </td>
                        <td className="py-1 pr-3 text-right">{r.expected_monthly_pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  const cls = tone === "good" ? "text-emerald-400" : tone === "bad" ? "text-rose-400"
    : tone === "warn" ? "text-amber-400" : "text-fg";
  return (
    <div className="bg-bg2 border border-border rounded p-2">
      <div className="text-xs text-muted">{label}</div>
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
